import { useRef, type MouseEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import profileImg from '@id-sk/frontend/idsk/assets/images/header-web/profile.svg';
import { useIdskModule } from './useIdskModule';
import { cx } from './cx';

export interface HeaderLanguage {
  code: string;
  label: string;
  selected: boolean;
}

export interface HeaderNavItem {
  to: string;
  text: string;
  active: boolean;
}

export interface HeaderWebProps {
  title: string;
  languages: HeaderLanguage[];
  onLanguageChange: (code: string) => void;
  navigation: HeaderNavItem[];
  userName?: string;
  onLogout: () => void;
}

const loadHeaderWeb = () =>
  import('@id-sk/frontend/idsk/components/header-web/header-web.js').then(
    (m) => (m as { default: new (el: Element) => { init: () => void } }).default,
  );

/**
 * idsk/components/header-web (headerType "website": title, language switcher, login area with the user name and
 * logout, navigation bar). The ID-SK script (dropdowns, mobile menu) is initialized on the root element; remount the
 * component (React `key`) when its structure changes.
 */
export function HeaderWeb({ title, languages, onLanguageChange, navigation, userName, onLogout }: HeaderWebProps) {
  const { t } = useTranslation();
  const ref = useRef<HTMLElement>(null);
  useIdskModule(ref, loadHeaderWeb);
  const current = languages.find((l) => l.selected) ?? languages[0];
  const showLanguages = t('header.showLanguageMenu', { language: current?.label ?? '' });
  const hideLanguages = t('header.hideLanguageMenu', { language: current?.label ?? '' });

  const logout = (event: MouseEvent) => {
    event.preventDefault();
    onLogout();
  };

  const mainButtons = (
    <div className="idsk-header-web__main--buttons">
      <div className={cx('idsk-header-web__main--login', userName && 'idsk-header-web__main--login--loggedIn')}>
        <button type="button" className="idsk-button idsk-header-web__main--login-loginbtn" data-module="idsk-button">
          {t('header.login')}
        </button>
        <div className="idsk-header-web__main--login-action">
          <img className="idsk-header-web__main--login-action-profile-img" src={profileImg} alt="" />
          <div className="idsk-header-web__main--login-action-text">
            <span className="govuk-body-s idsk-header-web__main--login-action-text-user-name" data-testid="header-user-name">
              {userName}
            </span>
            <div className="govuk-!-margin-bottom-1">
              <a
                className="govuk-link idsk-header-web__main--login-action-text-logout idsk-header-web__main--login-logoutbtn"
                href="/logout"
                onClick={logout}
              >
                {t('header.logout')}
              </a>
            </div>
          </div>
        </div>
        <button type="button" className="idsk-button idsk-header-web__main--login-logoutbtn" data-module="idsk-button" onClick={logout}>
          {t('header.logoutButton')}
        </button>
      </div>
    </div>
  );

  return (
    <header className="idsk-header-web" data-module="idsk-header-web" ref={ref}>
      <div className="idsk-header-web__scrolling-wrapper">
        <div className="idsk-header-web__tricolor" />
        <div className="idsk-header-web__brand">
          <div className="govuk-width-container">
            <div className="govuk-grid-row">
              <div className="govuk-grid-column-full">
                <div className="idsk-header-web__brand-spacer" />
                <div className="idsk-header-web__brand-language">
                  <button
                    type="button"
                    className="idsk-header-web__brand-language-button"
                    aria-label={showLanguages}
                    aria-expanded="false"
                    data-text-for-hide={hideLanguages}
                    data-text-for-show={showLanguages}
                  >
                    {current?.label}
                    <span className="idsk-header-web__link-arrow" />
                  </button>
                  <ul className="idsk-header-web__brand-language-list">
                    {[...languages]
                      .sort((a, b) => a.label.localeCompare(b.label))
                      .map((language) => (
                        <li className="idsk-header-web__brand-language-list-item" key={language.code}>
                          <a
                            className={cx(
                              'govuk-link idsk-header-web__brand-language-list-item-link',
                              language.selected && 'idsk-header-web__brand-language-list-item-link--selected',
                            )}
                            href={`?lang=${language.code}`}
                            lang={language.code}
                            hrefLang={language.code}
                            aria-current={language.selected ? 'true' : undefined}
                            onClick={(e) => {
                              e.preventDefault();
                              onLanguageChange(language.code);
                            }}
                          >
                            {language.label}
                          </a>
                        </li>
                      ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="idsk-header-web__main">
          <div className="govuk-width-container">
            <div className="govuk-grid-row">
              <div className="govuk-grid-column-full govuk-grid-column-one-third-from-desktop">
                <div className="idsk-header-web__main-headline">
                  <Link to="/" title={t('header.homeLinkTitle')}>
                    <h2 className="govuk-heading-m">{title}</h2>
                  </Link>
                  {navigation.length > 0 && (
                    <button
                      type="button"
                      className="idsk-button idsk-header-web__main-headline-menu-button"
                      aria-label={t('header.showMenu')}
                      aria-expanded="false"
                      data-text-for-show={t('header.showMenu')}
                      data-text-for-hide={t('header.hideMenu')}
                      data-text-for-close={t('header.closeMenu')}
                    >
                      {t('header.menu')}
                      <span className="idsk-header-web__menu-open" />
                      <span className="idsk-header-web__menu-close" />
                    </button>
                  )}
                </div>
              </div>
              <div className="govuk-grid-column-two-thirds">
                <div className="idsk-header-web__main-action">{mainButtons}</div>
              </div>
            </div>
          </div>
        </div>

        {navigation.length > 0 && <div className="idsk-header-web__nav--divider" />}
        <div className="idsk-header-web__nav idsk-header-web__nav--mobile">
          <div className="govuk-width-container">
            <div className="govuk-grid-row">
              <div className="govuk-grid-column-full" />
              {navigation.length > 0 && (
                <div className="govuk-grid-column-full">
                  <nav className="idsk-header-web__nav-bar--buttons" aria-label={t('header.mainNavigation')}>
                    <ul className="idsk-header-web__nav-list">
                      {navigation.map((item) => (
                        <li className="idsk-header-web__nav-list-item" key={item.to}>
                          <Link
                            className="govuk-link idsk-header-web__nav-list-item-link"
                            to={item.to}
                            title={item.text}
                            aria-current={item.active ? 'page' : undefined}
                          >
                            {item.text}
                          </Link>
                        </li>
                      ))}
                    </ul>
                  </nav>
                </div>
              )}
            </div>
            <div className="govuk-grid-row">
              <div className="govuk-grid-column-full">{mainButtons}</div>
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
